import { useState, useCallback } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useAccounting } from "@/contexts/AccountingContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  Download, FileSpreadsheet, FileText, FileJson, FileType, CheckCircle2,
  BookOpen, Receipt, Package, ShieldCheck, BarChart3, Loader2, History
} from "lucide-react";

// ========================
// TYPES
// ========================

type ExportFormat = "excel" | "csv" | "pdf" | "json";
type ExportModule = "ledgers" | "invoices" | "reports" | "inventory" | "gst_reports";

interface ExportConfig {
  module: ExportModule;
  label: string;
  description: string;
  icon: React.ReactNode;
  fields: { key: string; label: string }[];
}

interface ExportHistoryEntry {
  id: string;
  date: string;
  module: ExportModule;
  format: ExportFormat;
  records: number;
  fileSize: string;
  status: "Completed" | "Failed";
}

// ========================
// MODULE CONFIGS
// ========================

const EXPORT_MODULES: ExportConfig[] = [
  {
    module: "ledgers",
    label: "Ledgers",
    description: "Chart of accounts with balances",
    icon: <BookOpen className="h-5 w-5" />,
    fields: [
      { key: "name", label: "Name" },
      { key: "group", label: "Group" },
      { key: "nature", label: "Nature" },
      { key: "currentBalance", label: "Balance" },
      { key: "currentBalanceType", label: "Dr/Cr" },
      { key: "openingBalance", label: "Opening Balance" },
      { key: "openingBalanceType", label: "Opening Dr/Cr" },
      { key: "gstin", label: "GSTIN" },
    ],
  },
  {
    module: "invoices",
    label: "Invoices & Vouchers",
    description: "Sales, purchase & journal vouchers",
    icon: <Receipt className="h-5 w-5" />,
    fields: [
      { key: "voucherNumber", label: "Voucher No." },
      { key: "type", label: "Type" },
      { key: "date", label: "Date" },
      { key: "partyName", label: "Party" },
      { key: "totalDebit", label: "Debit" },
      { key: "totalCredit", label: "Credit" },
      { key: "status", label: "Status" },
      { key: "narration", label: "Narration" },
      { key: "invoiceNumber", label: "Invoice No." },
      { key: "gstAmount", label: "GST Amount" },
    ],
  },
  {
    module: "reports",
    label: "Financial Reports",
    description: "Trial balance, P&L, balance sheet summaries",
    icon: <BarChart3 className="h-5 w-5" />,
    fields: [
      { key: "ledgerName", label: "Ledger" },
      { key: "group", label: "Group" },
      { key: "nature", label: "Nature" },
      { key: "debit", label: "Debit" },
      { key: "credit", label: "Credit" },
      { key: "closingBalance", label: "Closing Balance" },
      { key: "balanceType", label: "Dr/Cr" },
    ],
  },
  {
    module: "inventory",
    label: "Inventory",
    description: "Stock items, groups & valuations",
    icon: <Package className="h-5 w-5" />,
    fields: [
      { key: "name", label: "Item Name" },
      { key: "group", label: "Group" },
      { key: "unit", label: "Unit" },
      { key: "quantity", label: "Quantity" },
      { key: "rate", label: "Rate" },
      { key: "value", label: "Value" },
      { key: "hsnCode", label: "HSN Code" },
      { key: "gstRate", label: "GST %" },
    ],
  },
  {
    module: "gst_reports",
    label: "GST Reports",
    description: "GSTR-1, GSTR-3B & HSN summary",
    icon: <ShieldCheck className="h-5 w-5" />,
    fields: [
      { key: "invoiceNumber", label: "Invoice No." },
      { key: "date", label: "Date" },
      { key: "partyName", label: "Party" },
      { key: "gstin", label: "GSTIN" },
      { key: "taxableValue", label: "Taxable Value" },
      { key: "cgst", label: "CGST" },
      { key: "sgst", label: "SGST" },
      { key: "igst", label: "IGST" },
      { key: "totalTax", label: "Total Tax" },
      { key: "invoiceValue", label: "Invoice Value" },
    ],
  },
];

const FORMAT_INFO: Record<ExportFormat, { label: string; ext: string; icon: React.ReactNode }> = {
  excel: { label: "Excel (.xlsx)", ext: "xlsx", icon: <FileSpreadsheet className="h-4 w-4" /> },
  csv: { label: "CSV (.csv)", ext: "csv", icon: <FileText className="h-4 w-4" /> },
  pdf: { label: "PDF (.pdf)", ext: "pdf", icon: <FileType className="h-4 w-4" /> },
  json: { label: "JSON (.json)", ext: "json", icon: <FileJson className="h-4 w-4" /> },
};

const SEED_HISTORY: ExportHistoryEntry[] = [];

const fmt = (n: number) => `₹${n.toLocaleString("en-IN")}`;

// ========================
// EXPORT HELPERS
// ========================

function getModuleData(module: ExportModule, ledgers: any[], vouchers: any[], stockItems: any[]): Record<string, any>[] {
  switch (module) {
    case "ledgers":
      return ledgers.map((l) => ({
        name: l.name, group: l.group, nature: l.nature,
        currentBalance: l.currentBalance, currentBalanceType: l.currentBalanceType,
        openingBalance: l.openingBalance, openingBalanceType: l.openingBalanceType,
        gstin: l.gstin || "",
      }));
    case "invoices":
      return vouchers.map((v) => ({
        voucherNumber: v.voucherNumber, type: v.type, date: v.date,
        partyName: v.partyName || "", totalDebit: v.totalDebit, totalCredit: v.totalCredit,
        status: v.status, narration: v.narration, invoiceNumber: v.invoiceNumber || "",
        gstAmount: v.gstAmount || 0,
      }));
    case "reports":
      return ledgers.map((l) => ({
        ledgerName: l.name, group: l.group, nature: l.nature,
        debit: l.currentBalanceType === "Dr" ? l.currentBalance : 0,
        credit: l.currentBalanceType === "Cr" ? l.currentBalance : 0,
        closingBalance: l.currentBalance, balanceType: l.currentBalanceType,
      }));
    case "inventory":
      return stockItems.map((i) => ({
        name: i.name, group: i.group || "", unit: i.unit || "",
        quantity: i.openingQty || i.quantity || 0, rate: i.openingRate || i.rate || 0,
        value: (i.openingQty || i.quantity || 0) * (i.openingRate || i.rate || 0),
        hsnCode: i.hsnCode || "", gstRate: i.gstRate || "",
      }));
    case "gst_reports":
      return vouchers
        .filter((v) => (v.type === "Sales" || v.type === "Purchase") && v.gstAmount)
        .map((v) => ({
          invoiceNumber: v.invoiceNumber || v.voucherNumber, date: v.date,
          partyName: v.partyName || "", gstin: "",
          taxableValue: v.totalDebit - (v.gstAmount || 0),
          cgst: v.cgst || 0, sgst: v.sgst || 0, igst: v.igst || 0,
          totalTax: v.gstAmount || 0, invoiceValue: v.totalDebit,
        }));
    default:
      return [];
  }
}

function downloadFile(content: string | ArrayBuffer | Blob, fileName: string, mimeType: string) {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

function exportToCSV(data: Record<string, any>[], fields: { key: string; label: string }[]): string {
  const header = fields.map((f) => f.label).join(",");
  const rows = data.map((row) => fields.map((f) => {
    const val = String(row[f.key] ?? "");
    return val.includes(",") ? `"${val}"` : val;
  }).join(","));
  return [header, ...rows].join("\n");
}

function exportToExcel(data: Record<string, any>[], fields: { key: string; label: string }[], sheetName: string): ArrayBuffer {
  const ws_data = [fields.map((f) => f.label), ...data.map((row) => fields.map((f) => row[f.key] ?? ""))];
  const ws = XLSX.utils.aoa_to_sheet(ws_data);
  // Auto column widths
  ws["!cols"] = fields.map((f) => ({ wch: Math.max(f.label.length, 15) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  return XLSX.write(wb, { bookType: "xlsx", type: "array" });
}

function exportToPDF(data: Record<string, any>[], fields: { key: string; label: string }[], title: string) {
  const doc = new jsPDF({ orientation: "landscape" });
  doc.setFontSize(16);
  doc.text(title, 14, 15);
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`Generated: ${new Date().toLocaleDateString("en-IN")} | AccouPro by hSECURITIES`, 14, 22);

  autoTable(doc, {
    startY: 28,
    head: [fields.map((f) => f.label)],
    body: data.map((row) => fields.map((f) => String(row[f.key] ?? ""))),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [30, 30, 30], textColor: 255, fontSize: 8 },
    alternateRowStyles: { fillColor: [248, 248, 248] },
  });

  return doc.output("blob");
}

// ========================
// PAGE COMPONENT
// ========================

export default function ExportPage() {
  const { ledgers, vouchers, stockItems } = useAccounting();
  const { toast } = useToast();

  const [selectedModules, setSelectedModules] = useState<Set<ExportModule>>(new Set(["ledgers"]));
  const [format, setFormat] = useState<ExportFormat>("excel");
  const [dateFrom, setDateFrom] = useState("2025-04-01");
  const [dateTo, setDateTo] = useState("2026-03-31");
  const [exporting, setExporting] = useState(false);
  const [history, setHistory] = useState<ExportHistoryEntry[]>(SEED_HISTORY);
  const [mainTab, setMainTab] = useState("export");

  const toggleModule = (mod: ExportModule) => {
    setSelectedModules((prev) => {
      const next = new Set(prev);
      if (next.has(mod)) next.delete(mod); else next.add(mod);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedModules.size === EXPORT_MODULES.length) setSelectedModules(new Set());
    else setSelectedModules(new Set(EXPORT_MODULES.map((m) => m.module)));
  };

  const handleExport = useCallback(async () => {
    if (selectedModules.size === 0) {
      toast({ title: "No modules selected", variant: "destructive" });
      return;
    }

    setExporting(true);

    // Small delay for UX
    await new Promise((r) => setTimeout(r, 800));

    try {
      const modules = Array.from(selectedModules);

      for (const mod of modules) {
        const config = EXPORT_MODULES.find((m) => m.module === mod)!;
        const data = getModuleData(mod, ledgers, vouchers, stockItems);
        const fileName = `accoupro_${mod}_${new Date().toISOString().split("T")[0]}`;

        switch (format) {
          case "csv": {
            const csv = exportToCSV(data, config.fields);
            downloadFile(csv, `${fileName}.csv`, "text/csv");
            break;
          }
          case "excel": {
            const buffer = exportToExcel(data, config.fields, config.label);
            downloadFile(new Blob([buffer]), `${fileName}.xlsx`, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
            break;
          }
          case "pdf": {
            const blob = exportToPDF(data, config.fields, `${config.label} Export — AccouPro`);
            downloadFile(blob, `${fileName}.pdf`, "application/pdf");
            break;
          }
          case "json": {
            const json = JSON.stringify(data, null, 2);
            downloadFile(json, `${fileName}.json`, "application/json");
            break;
          }
        }

        // Add to history
        const sizeEstimate = format === "pdf" ? `${Math.round(data.length * 1.5)} KB` :
          format === "excel" ? `${Math.round(data.length * 0.8)} KB` :
          `${Math.round(data.length * 0.5)} KB`;

        setHistory((prev) => [{
          id: `exp-${crypto.randomUUID().slice(0, 6)}`,
          date: new Date().toISOString().split("T")[0],
          module: mod,
          format,
          records: data.length,
          fileSize: sizeEstimate,
          status: "Completed",
        }, ...prev]);
      }

      toast({
        title: `Export Complete`,
        description: `${modules.length} module(s) exported as ${FORMAT_INFO[format].label}`,
      });
    } catch {
      toast({ title: "Export Failed", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  }, [selectedModules, format, ledgers, vouchers, stockItems, toast]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">Data Export</h1>
          <p className="text-sm text-muted-foreground">Export accounting data to Excel, CSV, PDF & JSON</p>
        </div>
        <Button onClick={handleExport} disabled={exporting || selectedModules.size === 0} className="gap-2">
          {exporting ? <><Loader2 className="h-4 w-4 animate-spin" /> Exporting...</> : <><Download className="h-4 w-4" /> Export {selectedModules.size > 1 ? `(${selectedModules.size} modules)` : ""}</>}
        </Button>
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList>
          <TabsTrigger value="export" className="gap-1.5"><Download className="h-3.5 w-3.5" /> Export</TabsTrigger>
          <TabsTrigger value="history" className="gap-1.5"><History className="h-3.5 w-3.5" /> Export History</TabsTrigger>
        </TabsList>

        <TabsContent value="export">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Module Selection */}
            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Select Modules</CardTitle>
                    <CardDescription>Choose data modules to export</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={selectAll}>
                    {selectedModules.size === EXPORT_MODULES.length ? "Deselect All" : "Select All"}
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {EXPORT_MODULES.map((config) => {
                      const isSelected = selectedModules.has(config.module);
                      const data = getModuleData(config.module, ledgers, vouchers, stockItems);
                      return (
                        <div
                          key={config.module}
                          onClick={() => toggleModule(config.module)}
                          className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                            isSelected ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"
                          }`}
                        >
                          <Checkbox checked={isSelected} className="mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-primary">{config.icon}</span>
                              <span className="font-medium text-foreground">{config.label}</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{config.description}</p>
                            <p className="text-xs text-muted-foreground mt-1">{data.length} records available</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Fields Preview */}
              {selectedModules.size > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Export Fields Preview</CardTitle>
                    <CardDescription>Fields included in your export</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Array.from(selectedModules).map((mod) => {
                        const config = EXPORT_MODULES.find((m) => m.module === mod)!;
                        return (
                          <div key={mod}>
                            <p className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                              {config.icon} {config.label}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {config.fields.map((f) => (
                                <Badge key={f.key} variant="secondary" className="text-xs">{f.label}</Badge>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Export Settings */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Export Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Format</Label>
                    <Select value={format} onValueChange={(v) => setFormat(v as ExportFormat)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(FORMAT_INFO).map(([k, v]) => (
                          <SelectItem key={k} value={k}>
                            <div className="flex items-center gap-2">{v.icon} {v.label}</div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Date From</Label>
                    <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                  </div>
                  <div>
                    <Label>Date To</Label>
                    <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Modules</span>
                    <span className="font-medium text-foreground">{selectedModules.size}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Records</span>
                    <span className="font-medium text-foreground">
                      {Array.from(selectedModules).reduce((s, m) => s + getModuleData(m, ledgers, vouchers, stockItems).length, 0).toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Format</span>
                    <span className="font-medium text-foreground">{FORMAT_INFO[format].label}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Period</span>
                    <span className="font-medium text-foreground">{dateFrom} to {dateTo}</span>
                  </div>
                  <Button onClick={handleExport} disabled={exporting || selectedModules.size === 0} className="w-full gap-2 mt-2">
                    {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    {exporting ? "Exporting..." : `Export ${selectedModules.size > 1 ? "Bulk" : ""}`}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Export History</CardTitle>
              <CardDescription>Previous data exports</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Module</TableHead>
                    <TableHead>Format</TableHead>
                    <TableHead className="text-right">Records</TableHead>
                    <TableHead className="text-right">Size</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((h) => {
                    const config = EXPORT_MODULES.find((m) => m.module === h.module);
                    return (
                      <TableRow key={h.id}>
                        <TableCell className="text-sm">{h.date}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {config?.icon}
                            <span className="font-medium text-foreground">{config?.label}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="gap-1">
                            {FORMAT_INFO[h.format].icon} {FORMAT_INFO[h.format].label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">{h.records.toLocaleString("en-IN")}</TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">{h.fileSize}</TableCell>
                        <TableCell>
                          <Badge variant={h.status === "Completed" ? "default" : "destructive"}>{h.status}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
